import * as XLSX from "xlsx";

const toDate = (value) => {
  if (!value) return null;

  if (value?.toDate) return value.toDate();

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
  const date = toDate(value);
  if (!date) return "";

  return date.toLocaleDateString("en-GB");
};

const getMonthKeyFromDate = (value) => {
  const date = toDate(value);
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");

  return `${year}-${month}`;
};

const getMonthLabel = (monthKey, format = "long") => {
  if (!monthKey) return "All Cases";

  const [year, month] = monthKey.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);

  return date.toLocaleDateString("en-GB", {
    month: format,
    year: "numeric",
  });
};

const getQuarterKeyFromDate = (value) => {
  const date = toDate(value);
  if (!date) return "";

  const year = date.getFullYear();
  const quarter = Math.ceil((date.getMonth() + 1) / 3);

  return `${year}-Q${quarter}`;
};

const getQuarterLabel = (quarterKey) => {
  if (!quarterKey) return "All Cases";

  const [year, q] = quarterKey.split("-");

  return `${q} ${year}`;
};

const safeSheetName = (name) => {
  return String(name || "Cases")
    .replace(/[\\/?*[\]:]/g, "")
    .slice(0, 31);
};

const moneyToNumber = (value) => {
  if (value === null || value === undefined || value === "") return "";

  const number = Number(value);
  return Number.isNaN(number) ? "" : number;
};

const getClientNames = (item) => {
  if (Array.isArray(item.clients) && item.clients.length) {
    return item.clients
      .map(
        (client) =>
          client.name ||
          `${client.firstName || ""} ${client.lastName || ""}`.trim(),
      )
      .filter(Boolean)
      .join(" + ");
  }

  return item.client?.name || "";
};

const getTasks = (item) => {
  if (Array.isArray(item.tasksForCase) && item.tasksForCase.length) {
    return item.tasksForCase.join(", ");
  }

  if (Array.isArray(item.taskIds) && item.taskIds.length) {
    return item.taskIds.join(", ");
  }

  return "";
};

const getAdditionalCost = (item) => {
  const additionalCost = Number(item.costs?.additionalCost || 0);

  if (additionalCost) return additionalCost;

  const modifiers = item.chargeSnapshot?.modifiers || [];

  if (!modifiers.length) return "";

  return modifiers.reduce((total, modifier) => {
    const amount = Number(modifier.amount || 0);
    const quantity = Number(modifier.quantity || 1);
    const modifierTotal = amount * quantity;

    return modifier.isDiscount ? total - modifierTotal : total + modifierTotal;
  }, 0);
};

const getEffectiveCompletedDate = (item) => {
  return item?.completionDate || item?.startDate || item?.createdAt || "";
};

const getCompletedByName = (item) => {
  return item?.assignedTo?.name || item?.assignedTo?.email || "Unassigned";
};

const getCaseTotal = (item) => {
  // Prefer the stored totalCost — it is the authoritative figure calculated
  // at save time and already accounts for discounts and additional charges.
  const stored = item?.costs?.totalCost;
  if (stored !== undefined && stored !== null && stored !== "") {
    return Number(stored) || 0;
  }

  // Fallback: recalculate from components (handles legacy / imported cases
  // that may not have totalCost populated).
  const baseCost = Number(item?.costs?.baseCost || 0);
  const additionalCost = getAdditionalCost(item);
  const additionalNum = additionalCost === "" ? 0 : Number(additionalCost) || 0;

  return baseCost + additionalNum;
};

const buildCompletedBySummary = (cases = []) => {
  // Group cases by quarter, then by month within each quarter
  const quarterData = {};

  cases.forEach((item) => {
    const quarterKey = getQuarterKeyFromDate(getEffectiveCompletedDate(item));
    const monthKey = getMonthKeyFromDate(getEffectiveCompletedDate(item));
    if (!quarterKey || !monthKey) return;

    if (!quarterData[quarterKey]) {
      quarterData[quarterKey] = { monthKeySet: new Set(), totalsByPerson: {} };
    }

    quarterData[quarterKey].monthKeySet.add(monthKey);

    const completedBy = getCompletedByName(item);
    const amount = getCaseTotal(item);

    if (!quarterData[quarterKey].totalsByPerson[completedBy]) {
      quarterData[quarterKey].totalsByPerson[completedBy] = {
        completedBy,
        months: {},
        grandTotal: 0,
      };
    }

    quarterData[quarterKey].totalsByPerson[completedBy].months[monthKey] =
      (quarterData[quarterKey].totalsByPerson[completedBy].months[monthKey] ||
        0) + amount;
    quarterData[quarterKey].totalsByPerson[completedBy].grandTotal += amount;
  });

  const quarterKeys = Object.keys(quarterData).sort();
  const aoa = [];
  let maxCols = 0;

  quarterKeys.forEach((quarterKey, qIndex) => {
    const { monthKeySet, totalsByPerson } = quarterData[quarterKey];
    const monthKeys = Array.from(monthKeySet).sort();
    const monthLabels = monthKeys.map((mk) => getMonthLabel(mk, "short"));

    const people = Object.values(totalsByPerson).sort((a, b) =>
      a.completedBy.localeCompare(b.completedBy),
    );

    // Quarter title row
    aoa.push([getQuarterLabel(quarterKey)]);

    // Header row
    const headerRow = ["Completed By", ...monthLabels, "Grand Total"];
    aoa.push(headerRow);
    maxCols = Math.max(maxCols, headerRow.length);

    // Person rows
    people.forEach((person) => {
      const row = [person.completedBy];
      monthKeys.forEach((mk) => row.push(person.months[mk] || ""));
      row.push(person.grandTotal || "");
      aoa.push(row);
    });

    // Grand Total row
    const grandTotalRow = ["Grand Total"];
    monthKeys.forEach((mk) => {
      const total = people.reduce(
        (sum, p) => sum + Number(p.months[mk] || 0),
        0,
      );
      grandTotalRow.push(total || "");
    });
    grandTotalRow.push(
      people.reduce((sum, p) => sum + Number(p.grandTotal || 0), 0) || "",
    );
    aoa.push(grandTotalRow);

    // Blank separator between quarters
    if (qIndex < quarterKeys.length - 1) {
      aoa.push([]);
    }
  });

  return { aoa, maxCols };
};

const buildRows = (cases = []) => {
  return cases.map((item) => {
    const baseCost = moneyToNumber(item.costs?.baseCost);
    const additionalCost = moneyToNumber(getAdditionalCost(item));
    const total = (Number(baseCost) || 0) + (Number(additionalCost) || 0);

    return {
      Date: formatDate(item.startDate || item.createdAt),
      Adviser: item.adviser?.name || "",
      Client: getClientNames(item),
      Type: item.type || item.chargeSnapshot?.itemName || "",
      Completed: "Yes",
      "Completion Date": formatDate(getEffectiveCompletedDate(item)),
      Notes: item.notes || "",
      Cost: baseCost,
      Additional: additionalCost,
      Total: total || "",
      "Completed By": item.assignedTo?.name || item.assignedTo?.email || "",
      "Task for case": getTasks(item),
    };
  });
};

const addWorksheetWidths = (worksheet) => {
  worksheet["!cols"] = [
    { wch: 12 },
    { wch: 22 },
    { wch: 34 },
    { wch: 26 },
    { wch: 12 },
    { wch: 16 },
    { wch: 40 },
    { wch: 12 },
    { wch: 14 },
    { wch: 14 },
    { wch: 22 },
    { wch: 100 },
  ];
};

export const exportParaplanningCasesToExcel = ({
  cases = [],
  monthKey = "",
  filePrefix = "Paraplanning Worksheet",
}) => {
  const monthLabel = getMonthLabel(monthKey);
  const casesForMonth = cases.filter((item) => {
    if (item.status !== "completed") return false;
    if (!monthKey) return true;

    return getMonthKeyFromDate(getEffectiveCompletedDate(item)) === monthKey;
  });

  if (!casesForMonth.length) {
    alert(`No completed cases found for ${monthLabel}.`);
    return;
  }

  const worksheet = XLSX.utils.json_to_sheet(buildRows(casesForMonth));
  addWorksheetWidths(worksheet);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName(monthLabel));

  XLSX.writeFile(workbook, `${filePrefix} - ${monthLabel}.xlsx`);
};

export const exportAllParaplanningMonthsToExcel = ({
  cases = [],
  filePrefix = "Paraplanning Worksheet",
}) => {
  const completedCases = cases
    .filter((item) => item.status === "completed")
    .sort((a, b) => {
      const aDate = toDate(getEffectiveCompletedDate(a));
      const bDate = toDate(getEffectiveCompletedDate(b));

      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;

      return aDate - bDate;
    });

  if (!completedCases.length) {
    alert("No completed cases found to export.");
    return;
  }

  const rows = completedCases.map((item) => {
    const baseCost = moneyToNumber(item.costs?.baseCost);
    const additionalCost = moneyToNumber(getAdditionalCost(item));
    const total = (Number(baseCost) || 0) + (Number(additionalCost) || 0);

    return {
      Month: getMonthLabel(
        getMonthKeyFromDate(getEffectiveCompletedDate(item)),
        "short",
      ),
      Date: formatDate(item.startDate || item.createdAt),
      Adviser: item.adviser?.name || "",
      Client: getClientNames(item),
      Type: item.type || item.chargeSnapshot?.itemName || "",
      Completed: "Yes",
      "Completion Date": formatDate(getEffectiveCompletedDate(item)),
      Notes: item.notes || "",
      Cost: baseCost,
      Additional: additionalCost,
      Total: total || "",
      "Completed By": item.assignedTo?.name || item.assignedTo?.email || "",
      "Task for case": getTasks(item),
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(rows);

  worksheet["!cols"] = [
    { wch: 14 }, // Month
    { wch: 12 }, // Date
    { wch: 22 }, // Adviser
    { wch: 34 }, // Client
    { wch: 26 }, // Type
    { wch: 12 }, // Completed
    { wch: 16 }, // Completion Date
    { wch: 40 }, // Notes
    { wch: 12 }, // Cost
    { wch: 14 }, // Additional
    { wch: 14 }, // Total
    { wch: 22 }, // Completed By
    { wch: 100 }, // Task for case
  ];

  const workbook = XLSX.utils.book_new();

  const { aoa, maxCols } = buildCompletedBySummary(completedCases);

  const completedByWorksheet = XLSX.utils.aoa_to_sheet(aoa);
  completedByWorksheet["!cols"] = [
    { wch: 24 },
    ...Array.from({ length: maxCols - 1 }, () => ({ wch: 14 })),
  ];

  XLSX.utils.book_append_sheet(workbook, worksheet, "All Months");
  XLSX.utils.book_append_sheet(workbook, completedByWorksheet, "Completed By");

  XLSX.writeFile(workbook, `${filePrefix} - All Months.xlsx`);
};